import { useCallback } from 'react';
import { Button, ListItem, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import type { PersistentNotification } from '../types.ts';
import { CopyDebugInfoButton } from '@/feature/notifications/components/CopyDebugInfoButton';
import { dispatchToastAction } from '../actionRegistry';
import { SecondaryActionLink } from './SecondaryActionLink';

const severityIconMap = {
  success: <CheckCircleIcon color="success" fontSize="large" />,
  error: <CancelIcon color="error" fontSize="large" />,
  info: <InfoIcon color="info" fontSize="large" />,
  warning: <WarningIcon color="warning" fontSize="large" />,
  announcement: <CampaignIcon color="info" fontSize="large" />,
} as const;

const timestampFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function formatTimestamp(created: number) {
  return timestampFormatter.format(new Date(created));
}

function getPrimaryTextColor(severity: PersistentNotification['severity']) {
  if (severity === 'error') return 'error.main';
  if (severity === 'warning') return 'warning.main';
  return 'text.primary';
}

const listItemIconSx = {
  minWidth: 32,
  alignSelf: 'flex-start',
  mt: '4px',
};

const copyButtonSx = {
  alignSelf: 'flex-start',
  mt: 0,
};

interface NotificationListItemProps {
  notification: PersistentNotification;
}

export function NotificationListItem({ notification }: NotificationListItemProps) {
  const shouldShowCopyButton =
    Boolean(notification.debugInfo) || notification.severity === 'error' || notification.accountId != null;

  const { action, secondaryAction } = notification;
  const handleActionClick = useCallback(() => {
    if (action) dispatchToastAction(action);
  }, [action]);
  const handleSecondaryActionClick = useCallback(() => {
    if (secondaryAction) dispatchToastAction(secondaryAction);
  }, [secondaryAction]);

  return (
    <>
      <ListItem alignItems="flex-start" sx={{ px: 2, py: 1 }}>
        <ListItemIcon sx={listItemIconSx}>{severityIconMap[notification.severity]}</ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography
              variant="body2"
              sx={{ color: getPrimaryTextColor(notification.severity), mb: '4px', overflowWrap: 'anywhere' }}
            >
              {notification.message}
              {secondaryAction && <SecondaryActionLink action={secondaryAction} onClick={handleSecondaryActionClick} />}
            </Typography>
          }
          secondary={
            <>
              <Typography variant="body2" component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {formatTimestamp(notification.created)}
              </Typography>
              {action && (
                <Button
                  size="small"
                  variant="contained"
                  color={notification.severity === 'announcement' ? 'inherit' : notification.severity}
                  onClick={handleActionClick}
                  sx={{ ml: 1, py: 0, minWidth: 0, lineHeight: 1.4 }}
                >
                  {action.label}
                </Button>
              )}
            </>
          }
        />
        {shouldShowCopyButton && (
          <CopyDebugInfoButton
            message={notification.message}
            debugInfo={notification.debugInfo}
            sessionId={notification.sessionId}
            accountId={notification.accountId}
            timestamp={notification.created}
            sx={copyButtonSx}
          />
        )}
      </ListItem>
      <Divider />
    </>
  );
}
